import React, { useMemo } from 'react';
import { DomainHit, ProviderId } from '@/lib/trustHub/v1/types';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

interface MatrixViewProps {
  domains: DomainHit[];
  queries: string[];
  maxDomains?: number;
  onFocusQuery?: (query: string) => void;
  onFocusDomain?: (domain: string) => void;
}

interface HeatCell {
  isUsed: boolean;
  providers: Set<ProviderId>;
}

export const MatrixView: React.FC<MatrixViewProps> = ({
  domains,
  queries,
  maxDomains = 15,
  onFocusQuery,
  onFocusDomain,
}) => {
  const topDomains = domains.slice(0, maxDomains);

  // Generate heat cells with provider information
  const heatCells = useMemo(() => {
    return topDomains.map((domain) => {
      return queries.map((query): HeatCell => {
        const isUsed = domain.usedBy.has(query);
        
        // Determine which providers cited this domain for this query
        const providers = new Set<ProviderId>();
        
        // Check if this query is in the domain's usedBy set
        if (isUsed) {
          // Check which providers have this domain
          if (domain.providerBreakdown.openai && domain.providerBreakdown.openai.usedCount > 0) {
            providers.add('openai');
          }
          if (domain.providerBreakdown.claude && domain.providerBreakdown.claude.usedCount > 0) {
            providers.add('claude');
          }
        }
        
        return { isUsed, providers };
      });
    });
  }, [topDomains, queries]);

  // Calculate column totals (queries)
  const queryTotals = useMemo(() => {
    return queries.map((_, queryIdx) => {
      return topDomains.reduce((total, _, domainIdx) => {
        return total + (heatCells[domainIdx][queryIdx].isUsed ? 1 : 0);
      }, 0);
    });
  }, [queries, topDomains, heatCells]);

  // Calculate row totals (domains)
  const domainTotals = useMemo(() => {
    return topDomains.map((_, domainIdx) => {
      return queries.reduce((total, _, queryIdx) => {
        return total + (heatCells[domainIdx][queryIdx].isUsed ? 1 : 0);
      }, 0);
    });
  }, [topDomains, queries, heatCells]);

  // Calculate grand total
  const grandTotal = useMemo(() => {
    return heatCells.reduce((total, row) => {
      return total + row.reduce((rowTotal, cell) => rowTotal + (cell.isUsed ? 1 : 0), 0);
    }, 0);
  }, [heatCells]);

  // Get cell styling based on provider usage
  const getCellClass = (cell: HeatCell) => {
    if (!cell.isUsed) {
      return 'bg-background';
    }

    const hasOpenAI = cell.providers.has('openai');
    const hasClaude = cell.providers.has('claude');

    if (hasOpenAI && hasClaude) {
      return 'bg-blue-500 text-white font-bold';
    } else if (hasOpenAI) {
      return 'bg-green-500 text-white font-bold';
    } else if (hasClaude) {
      return 'bg-orange-500 text-white font-bold';
    }

    return 'bg-primary text-primary-foreground font-bold';
  };

  // Get tooltip content
  const getTooltip = (domain: DomainHit, query: string, cell: HeatCell) => {
    if (!cell.isUsed) {
      return `${domain.domain}\n${query}\nNo citation`;
    }

    const providerList = Array.from(cell.providers);
    const providerText = providerList.length > 0
      ? providerList.map(p => p.toUpperCase()).join(' & ')
      : 'Unknown';
    return `${domain.domain}\n${query}\nCited by ${providerText}`;
  };

  return (
    <TooltipProvider>
      <div className="space-y-4">
        {/* Legend */}
        <div className="flex flex-wrap gap-3 text-xs">
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-blue-500 rounded" />
            <span>Both OpenAI & Claude</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-green-500 rounded" />
            <span>OpenAI only</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-orange-500 rounded" />
            <span>Claude only</span>
          </div>
        </div>

        {/* Matrix Table */}
        <div className="overflow-x-auto">
          <div className="inline-block min-w-full">
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr>
                  <th className="sticky left-0 z-10 bg-background border p-2 text-left min-w-[200px]">
                    Domain
                  </th>
                  {queries.map((query, idx) => (
                    <th
                      key={idx}
                      className="border p-2 text-left min-w-[120px] max-w-[180px] cursor-pointer hover:bg-muted/50 transition-colors"
                      onClick={() => onFocusQuery?.(query)}
                      title={query}
                    >
                      <div className="truncate text-center text-xs">
                        {query}
                      </div>
                    </th>
                  ))}
                  <th className="border p-2 text-center bg-muted/20 font-bold min-w-[60px]">
                    Total
                  </th>
                </tr>
              </thead>
              <tbody>
                {topDomains.map((domain, domainIdx) => (
                  <tr key={domain.domain} className="hover:bg-muted/10">
                    <td 
                      className="sticky left-0 z-10 bg-background border p-2 font-medium cursor-pointer hover:bg-muted/50 transition-colors"
                      onClick={() => onFocusDomain?.(domain.domain)}
                    >
                      <div className="flex items-center gap-2">
                        <img
                          src={domain.favicon}
                          alt=""
                          className="w-4 h-4 flex-shrink-0"
                          onError={(e) => {
                            e.currentTarget.src = 'https://www.google.com/s2/favicons?domain=example.com&sz=64';
                          }}
                        />
                        <span className="truncate">{domain.domain}</span>
                      </div>
                    </td>
                    {heatCells[domainIdx].map((cell, queryIdx) => (
                      <td
                        key={queryIdx}
                        className={`border p-2 text-center transition-all ${getCellClass(cell)}`}
                      >
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <div className="w-full h-full flex items-center justify-center">
                              {cell.isUsed ? '●' : ''}
                            </div>
                          </TooltipTrigger>
                          <TooltipContent>
                            <div className="text-xs whitespace-pre-line">
                              {getTooltip(domain, queries[queryIdx], cell)}
                            </div>
                          </TooltipContent>
                        </Tooltip>
                      </td>
                    ))}
                    <td className="border p-2 text-center bg-muted/10 font-semibold">
                      {domainTotals[domainIdx]}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="bg-muted/20">
                  <td className="sticky left-0 z-10 bg-muted/20 border p-2 font-bold">
                    Column Total
                  </td>
                  {queryTotals.map((total, idx) => (
                    <td key={idx} className="border p-2 text-center font-semibold">
                      {total}
                    </td>
                  ))}
                  <td className="border p-2 text-center font-bold">
                    {grandTotal}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      </div>
    </TooltipProvider>
  );
};
